// CommunicationLog.js - Unified Communication Log (Emails, Calls, SMS)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert,
  Stack, IconButton, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  Divider, Autocomplete
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Forum as CommIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
  Add as AddIcon,
  CallMade as OutboundIcon,
  CallReceived as InboundIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from '../../../../utils/axiosInstance';
import { emailSyncService, communicationService } from './activityCommService';
import { CommunicationDirectionLabels, formatDateTime } from './activityCommEnums';

const CommunicationLog = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tabValue, setTabValue] = useState(0); // 0=All, 1=Emails, 2=Calls, 3=SMS
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');

  // Dialogs
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Contact dropdown data
  const [contacts, setContacts] = useState([]);

  const [callForm, setCallForm] = useState({
    subject: '',
    description: '',
    contact_id: null,
    direction: 'OUTBOUND',
    duration: '',
    result: ''
  });

  const [smsForm, setSmsForm] = useState({
    contact_id: null,
    phone_number: '',
    message: '',
    direction: 'OUTBOUND'
  });

  useEffect(() => {
    document.title = 'Communications | Activity & Communications';
    checkPermissions();
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId, per_page: 100 } : { per_page: 100 };
      const res = await axiosInstance.get('/api/crm/contacts', { params });
      if (res.data.success) setContacts(res.data.data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  };

  useEffect(() => {
    if (canView) {
      loadData();
    }
  }, [canView, tabValue, page, rowsPerPage]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const buildParams = useCallback(() => {
    const params = {
      page: page + 1,
      per_page: rowsPerPage
    };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (contactSearch) params.contact = contactSearch;
    if (directionFilter) params.direction = directionFilter;
    return params;
  }, [page, rowsPerPage, dateFrom, dateTo, contactSearch, directionFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = buildParams();

      if (tabValue === 0) {
        // All - merge emails, calls, SMS
        const [emailRes, callRes, smsRes] = await Promise.allSettled([
          emailSyncService.getEmailLogs(params),
          communicationService.getCallLogs(params),
          communicationService.getSmsLogs(params)
        ]);

        const emails = (emailRes.status === 'fulfilled' ? (emailRes.value.data || []) : [])
          .map(e => ({ ...e, _type: 'email' }));
        const calls = (callRes.status === 'fulfilled' ? (callRes.value.data || []) : [])
          .map(c => ({ ...c, _type: 'call' }));
        const sms = (smsRes.status === 'fulfilled' ? (smsRes.value.data || []) : [])
          .map(s => ({ ...s, _type: 'sms' }));

        const merged = [...emails, ...calls, ...sms].sort(
          (a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0)
        );

        setEntries(merged);
        const emailTotal = emailRes.status === 'fulfilled' ? (emailRes.value.pagination?.total || emails.length) : 0;
        const callTotal = callRes.status === 'fulfilled' ? (callRes.value.pagination?.total || calls.length) : 0;
        const smsTotal = smsRes.status === 'fulfilled' ? (smsRes.value.pagination?.total || sms.length) : 0;
        setTotalCount(emailTotal + callTotal + smsTotal);
      } else if (tabValue === 1) {
        const res = await emailSyncService.getEmailLogs(params);
        setEntries((res.data || []).map(e => ({ ...e, _type: 'email' })));
        setTotalCount(res.pagination?.total || (res.data || []).length);
      } else if (tabValue === 2) {
        const res = await communicationService.getCallLogs(params);
        setEntries((res.data || []).map(c => ({ ...c, _type: 'call' })));
        setTotalCount(res.pagination?.total || (res.data || []).length);
      } else {
        const res = await communicationService.getSmsLogs(params);
        setEntries((res.data || []).map(s => ({ ...s, _type: 'sms' })));
        setTotalCount(res.pagination?.total || (res.data || []).length);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load communications', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setPage(0);
    loadData();
  };

  const handleLogCall = async () => {
    if (!callForm.subject) {
      enqueueSnackbar('Subject is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await communicationService.logCall({
        subject: callForm.subject,
        description: callForm.description,
        contact_id: callForm.contact_id,
        direction: callForm.direction,
        duration: callForm.duration ? parseInt(callForm.duration, 10) : null,
        result: callForm.result
      });
      enqueueSnackbar('Call logged successfully', { variant: 'success' });
      setCallDialogOpen(false);
      setCallForm({ subject: '', description: '', contact_id: null, direction: 'OUTBOUND', duration: '', result: '' });
      loadData();
    } catch (err) {
      enqueueSnackbar('Failed to log call', { variant: 'error' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogSms = async () => {
    if (!smsForm.message) {
      enqueueSnackbar('Message is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await communicationService.logSms({
        contact_id: smsForm.contact_id,
        phone_number: smsForm.phone_number,
        message_content: smsForm.message,
        direction: smsForm.direction
      });
      enqueueSnackbar('SMS logged successfully', { variant: 'success' });
      setSmsDialogOpen(false);
      setSmsForm({ contact_id: null, phone_number: '', message: '', direction: 'OUTBOUND' });
      loadData();
    } catch (err) {
      enqueueSnackbar('Failed to log SMS', { variant: 'error' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'email') return <EmailIcon sx={{ color: '#9c27b0', fontSize: 20 }} />;
    if (type === 'call') return <PhoneIcon sx={{ color: '#1976d2', fontSize: 20 }} />;
    return <SmsIcon sx={{ color: '#00897b', fontSize: 20 }} />;
  };

  const getDirectionIcon = (direction) => {
    if ((direction || '').toUpperCase() === 'INBOUND') {
      return <InboundIcon sx={{ color: '#2e7d32', fontSize: 18 }} />;
    }
    return <OutboundIcon sx={{ color: '#ed6c02', fontSize: 18 }} />;
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/activity-dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <CommIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Communications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unified view of emails, calls, and SMS
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<PhoneIcon />}
              onClick={() => setCallDialogOpen(true)}
            >
              Log Call
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SmsIcon />}
              onClick={() => setSmsDialogOpen(true)}
            >
              Log SMS
            </Button>
          </Stack>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => { setTabValue(v); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" />
          <Tab label="Emails" icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Calls" icon={<PhoneIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="SMS" icon={<SmsIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FilterIcon sx={{ color: 'text.secondary' }} />
          <TextField
            type="date"
            size="small"
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="Contact Search"
            value={contactSearch}
            onChange={(e) => setContactSearch(e.target.value)}
            sx={{ width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Direction</InputLabel>
            <Select
              value={directionFilter}
              label="Direction"
              onChange={(e) => setDirectionFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="INBOUND">Inbound</MenuItem>
              <MenuItem value="OUTBOUND">Outbound</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={handleFilter}>
            Apply
          </Button>
        </Stack>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CommIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No communications found</Typography>
          <Typography variant="body2" color="text.secondary">
            {tabValue === 0
              ? 'No emails, calls, or SMS have been logged yet.'
              : `No ${['', 'emails', 'calls', 'SMS'][tabValue]} found for the selected filters.`
            }
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={60}>Type</TableCell>
                  <TableCell width={60}>Dir</TableCell>
                  <TableCell>Subject / Summary</TableCell>
                  <TableCell width={180}>Contact</TableCell>
                  <TableCell width={160}>Date / Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow key={`${entry._type}-${entry.id || idx}`} hover>
                    <TableCell>{getTypeIcon(entry._type)}</TableCell>
                    <TableCell>{getDirectionIcon(entry.direction)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {entry.subject || entry.message || entry.summary || '-'}
                      </Typography>
                      {entry.description && (
                        <Typography variant="caption" color="text.secondary">
                          {entry.description.length > 80 ? entry.description.slice(0, 80) + '...' : entry.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {entry.contact_name || entry.contact || entry.phone_number || entry.from_address || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(entry.created_at || entry.date)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Log Call Dialog */}
      <Dialog open={callDialogOpen} onClose={() => setCallDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Call</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Subject"
              fullWidth
              required
              value={callForm.subject}
              onChange={(e) => setCallForm({ ...callForm, subject: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={callForm.description}
              onChange={(e) => setCallForm({ ...callForm, description: e.target.value })}
            />
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim();
              }}
              value={contacts.find(c => c.id === callForm.contact_id) || null}
              onChange={(_, value) => setCallForm({ ...callForm, contact_id: value?.id || null })}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Contact" placeholder="Select contact" fullWidth />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box>
                      <Typography variant="body2">
                        {option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </Box>
                );
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select
                value={callForm.direction}
                label="Direction"
                onChange={(e) => setCallForm({ ...callForm, direction: e.target.value })}
              >
                <MenuItem value="INBOUND">Inbound</MenuItem>
                <MenuItem value="OUTBOUND">Outbound</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Duration (minutes)"
              type="number"
              fullWidth
              value={callForm.duration}
              onChange={(e) => setCallForm({ ...callForm, duration: e.target.value })}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Result"
              fullWidth
              value={callForm.result}
              onChange={(e) => setCallForm({ ...callForm, result: e.target.value })}
              placeholder="e.g. Left voicemail, Connected, No answer"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLogCall} disabled={saving}>
            {saving ? 'Saving...' : 'Log Call'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log SMS Dialog */}
      <Dialog open={smsDialogOpen} onClose={() => setSmsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log SMS</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim();
              }}
              value={contacts.find(c => c.id === smsForm.contact_id) || null}
              onChange={(_, value) => {
                const contact = value;
                setSmsForm({
                  ...smsForm,
                  contact_id: contact?.id || null,
                  phone_number: smsForm.phone_number || contact?.phone || ''
                });
              }}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Contact" placeholder="Select contact" fullWidth />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box>
                      <Typography variant="body2">
                        {option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{option.phone || option.email}</Typography>
                    </Box>
                  </Box>
                );
              }}
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={smsForm.phone_number}
              onChange={(e) => setSmsForm({ ...smsForm, phone_number: e.target.value })}
            />
            <TextField
              label="Message"
              fullWidth
              required
              multiline
              rows={3}
              value={smsForm.message}
              onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select
                value={smsForm.direction}
                label="Direction"
                onChange={(e) => setSmsForm({ ...smsForm, direction: e.target.value })}
              >
                <MenuItem value="INBOUND">Inbound</MenuItem>
                <MenuItem value="OUTBOUND">Outbound</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLogSms} disabled={saving}>
            {saving ? 'Saving...' : 'Log SMS'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationLog;
